// US state / territory / military lookup, used by the Shopify draft
// order PUT path to produce the (province, province_code) pair Shopify
// REST actually persists.
//
// Why this exists: Shopify REST 2024-01 silently strips `province_code`
// when sent without `province` (the full name). Confirmed with a 4-way
// PUT diag against quotesense-demo draft #D12 — v1 (province_code-only)
// came back province=null, province_code=null even though Shopify
// geocoded the address correctly (lat/long resolved to PA). Sending
// `province` (full name) alone OR both together both persisted.
//
// The rep types a 2-letter code in the editor's state field (it's
// maxLength=2). So every PUT was using the broken shape until we
// added the lookup here.

export const US_STATE_CODE_TO_NAME: Record<string, string> = {
  AL: "Alabama", AK: "Alaska", AZ: "Arizona", AR: "Arkansas", CA: "California",
  CO: "Colorado", CT: "Connecticut", DE: "Delaware", DC: "District of Columbia",
  FL: "Florida", GA: "Georgia", HI: "Hawaii", ID: "Idaho", IL: "Illinois",
  IN: "Indiana", IA: "Iowa", KS: "Kansas", KY: "Kentucky", LA: "Louisiana",
  ME: "Maine", MD: "Maryland", MA: "Massachusetts", MI: "Michigan", MN: "Minnesota",
  MS: "Mississippi", MO: "Missouri", MT: "Montana", NE: "Nebraska", NV: "Nevada",
  NH: "New Hampshire", NJ: "New Jersey", NM: "New Mexico", NY: "New York",
  NC: "North Carolina", ND: "North Dakota", OH: "Ohio", OK: "Oklahoma", OR: "Oregon",
  PA: "Pennsylvania", RI: "Rhode Island", SC: "South Carolina", SD: "South Dakota",
  TN: "Tennessee", TX: "Texas", UT: "Utah", VT: "Vermont", VA: "Virginia",
  WA: "Washington", WV: "West Virginia", WI: "Wisconsin", WY: "Wyoming",
  // US territories — Shopify treats these as US "states" via country_code=US.
  PR: "Puerto Rico", GU: "Guam", VI: "U.S. Virgin Islands",
  AS: "American Samoa", MP: "Northern Mariana Islands",
  // APO / FPO military.
  AA: "Armed Forces Americas", AE: "Armed Forces Europe", AP: "Armed Forces Pacific",
};

// Reverse map for normalizing full-name input back to a 2-letter code.
// Lowercased keys for case-insensitive lookup.
export const US_STATE_NAME_TO_CODE: Record<string, string> = Object.fromEntries(
  Object.entries(US_STATE_CODE_TO_NAME).map(([code, name]) => [name.toLowerCase(), code]),
);

/**
 * Normalize a raw US state input into the (province, province_code)
 * pair Shopify REST will accept. Handles:
 *
 *   - 2-letter code:    "PA"            → { province: "Pennsylvania", province_code: "PA" }
 *   - full name:        "Pennsylvania"  → { province: "Pennsylvania", province_code: "PA" }
 *   - mixed case:       "pa"            → { province: "Pennsylvania", province_code: "PA" }
 *   - unknown 2-letter: "XX"            → { province_code: "XX" }     (let Shopify try)
 *   - unknown name:     "Atlantis"      → { province: "Atlantis" }    (let Shopify try)
 *   - empty / nullish:                  → {}
 *
 * Always returns BOTH fields when the input is recognized, matching
 * the diag's v3 shape that Shopify confirmed accepts cleanly.
 */
export function normalizeUSStateForShopify(raw: any): { province?: string; province_code?: string } {
  const s = (raw == null ? "" : String(raw)).trim();
  if (!s) return {};

  if (/^[A-Za-z]{2}$/.test(s)) {
    const code = s.toUpperCase();
    const full = US_STATE_CODE_TO_NAME[code];
    return full ? { province: full, province_code: code } : { province_code: code };
  }

  // Full name (or anything longer than 2 letters). Look up reverse map.
  const code = US_STATE_NAME_TO_CODE[s.toLowerCase()];
  if (code) {
    return { province: US_STATE_CODE_TO_NAME[code], province_code: code };
  }
  // Unknown — pass through as province. Shopify may still resolve it.
  return { province: s };
}
